import { HomeDashboardGrid, type HomeDashboardGridProps } from '../home/HomeDashboardGrid';
import { useId, useRef, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Columns2, Plus, Trash2 } from 'lucide-react';
import {
  Badge,
  EmptyState,
  IconButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  cn,
} from '@/components/ui';
import { layoutError, moveWidget, type WidgetPlacement } from './viewData';
export interface DashboardWidget {
  id: string;
  title: string;
  description?: string;
  content: ReactNode;
  loadingContent?: ReactNode;
}
export interface DashboardGridProps {
  /** Opt-in personal home grid. Existing overview props and defaults are preserved. */
  home?: HomeDashboardGridProps;
  label: string;
  widgets: readonly DashboardWidget[];
  layout: readonly WidgetPlacement[];
  editing?: boolean;
  readOnly?: boolean;
  /** Equal-height widgets within each row; content remains top-aligned. */
  equalRowHeight?: boolean;
  state?: 'ready' | 'loading' | 'error';
  onRetry?: () => void;
  onChange?: (next: WidgetPlacement[]) => void | Promise<void>;
}
export function DashboardGrid({
  label,
  widgets,
  layout,
  editing = false,
  readOnly = false,
  equalRowHeight = false,
  state = 'ready',
  onRetry,
  onChange,
  home,
}: DashboardGridProps) {
  const id = useId(),
    refs = useRef(new Map<string, HTMLElement>()),
    addTrigger = useRef<HTMLButtonElement>(null);
  const [pending, setPending] = useState(false),
    [error, setError] = useState(''),
    [announcement, setAnnouncement] = useState('');
  const structural = layoutError(
    layout,
    widgets.map((widget) => widget.id),
  );
  const canEdit = editing && !readOnly && !!onChange,
    unused = widgets.filter((widget) => !layout.some((item) => item.widgetId === widget.id));
  async function change(next: WidgetPlacement[], message: string, focusId?: string) {
    if (!canEdit || pending) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPending(true);
    setError('');
    try {
      await onChange!(next);
      setAnnouncement(message);
      requestAnimationFrame(() => {
        const target = focusId ? refs.current.get(focusId) : null;
        (target ?? addTrigger.current)?.focus();
      });
    } catch (issue) {
      setError(issue instanceof Error ? issue.message : 'Настройки не сохранены. Повтори действие.');
      requestAnimationFrame(() => {
        const target = previousFocus?.isConnected ? previousFocus : addTrigger.current;
        target?.focus();
      });
    } finally {
      setPending(false);
    }
  }
  if (home) return <HomeDashboardGrid {...home} />;
  if (state === 'error' || structural)
    return (
      <EmptyState
        title="Обзор недоступен"
        description={structural ?? 'Не удалось получить виджеты.'}
        action={onRetry ? { label: 'Повторить', onClick: onRetry } : undefined}
      />
    );
  if (!layout.length && state !== 'loading' && !canEdit)
    return <EmptyState title="В обзоре пока нет виджетов" description="Добавь виджеты в режиме настройки." />;
  return (
    <section
      data-slot="dashboard-grid"
      aria-label={label}
      aria-busy={pending || state === 'loading'}
      className="space-y-4"
    >
      <div className="sr-only" aria-live="polite">
        {announcement}
      </div>
      {error && (
        <p
          role="alert"
          className="rounded-md border border-danger-line bg-danger-soft p-3 text-sm text-danger-strong"
        >
          {error}
        </p>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-end gap-3 rounded-md border border-line bg-muted p-3">
          <div className="min-w-0 flex-1">
            <label id={id + '-add'} className="mb-1 block text-xs font-medium text-fg-muted">
              Добавить виджет
            </label>
            <Select
              value=""
              disabled={pending || !unused.length}
              onValueChange={(widgetId) =>
                void change([...layout, { widgetId, span: 'single' }], 'Виджет добавлен.', widgetId)
              }
            >
              <SelectTrigger ref={addTrigger} aria-labelledby={id + '-add'}>
                <SelectValue placeholder={unused.length ? 'Выбери из доступных' : 'Все виджеты добавлены'} />
              </SelectTrigger>
              <SelectContent>
                {unused.map((widget) => (
                  <SelectItem key={widget.id} value={widget.id}>
                    {widget.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Badge>{layout.length} в обзоре</Badge>
        </div>
      )}
      {!layout.length && state !== 'loading' && (
        <EmptyState title="Добавь первый виджет" icon={<Plus className="size-5" />} />
      )}
      <div className={cn('grid gap-4 md:grid-cols-2', equalRowHeight ? 'items-stretch' : 'items-start')}>
        {(state === 'loading' && !layout.length
          ? widgets.slice(0, 3).map((widget) => ({ widgetId: widget.id, span: 'single' as const }))
          : layout
        ).map((placement, index) => {
          const widget = widgets.find((item) => item.id === placement.widgetId)!;
          return (
            <article
              key={widget.id}
              ref={(element) => {
                if (element) refs.current.set(widget.id, element);
                else refs.current.delete(widget.id);
              }}
              tabIndex={canEdit ? 0 : undefined}
              aria-label={widget.title}
              data-widget-id={widget.id}
              data-widget-span={placement.span}
              className={cn(
                'min-w-0 rounded-lg border border-line bg-card p-4 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                placement.span === 'wide' && 'md:col-span-2',
              )}
            >
              <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="type-subheading">{widget.title}</h3>
                  {widget.description && <p className="mt-1 text-xs text-fg-muted">{widget.description}</p>}
                </div>
                {canEdit && (
                  <div className="flex shrink-0 gap-1">
                    <IconButton
                      type="button"
                      size="sm"
                      disabled={pending || index === 0}
                      aria-label={widget.title + ': выше'}
                      title="Выше"
                      onClick={() =>
                        void change(
                          moveWidget(layout, widget.id, -1),
                          widget.title + ': позиция ' + index,
                          widget.id,
                        )
                      }
                    >
                      <ArrowUp />
                    </IconButton>
                    <IconButton
                      type="button"
                      size="sm"
                      disabled={pending || index === layout.length - 1}
                      aria-label={widget.title + ': ниже'}
                      title="Ниже"
                      onClick={() =>
                        void change(
                          moveWidget(layout, widget.id, 1),
                          widget.title + ': позиция ' + (index + 2),
                          widget.id,
                        )
                      }
                    >
                      <ArrowDown />
                    </IconButton>
                    <IconButton
                      type="button"
                      size="sm"
                      disabled={pending}
                      aria-label={
                        widget.title + ': ' + (placement.span === 'wide' ? 'одна колонка' : 'две колонки')
                      }
                      aria-pressed={placement.span === 'wide'}
                      title="Ширина"
                      onClick={() =>
                        void change(
                          layout.map((item) =>
                            item.widgetId === widget.id
                              ? { ...item, span: item.span === 'wide' ? 'single' : 'wide' }
                              : item,
                          ),
                          'Ширина виджета изменена.',
                          widget.id,
                        )
                      }
                    >
                      <Columns2 />
                    </IconButton>
                    <IconButton
                      type="button"
                      size="sm"
                      disabled={pending}
                      aria-label={widget.title + ': убрать из обзора'}
                      title="Убрать из обзора"
                      onClick={() =>
                        void change(
                          layout.filter((item) => item.widgetId !== widget.id),
                          'Виджет убран из обзора. Его можно добавить снова.',
                          layout[index + 1]?.widgetId ?? layout[index - 1]?.widgetId,
                        )
                      }
                    >
                      <Trash2 />
                    </IconButton>
                  </div>
                )}
              </header>
              {state === 'loading'
                ? (widget.loadingContent ?? (
                    <div className="space-y-3">
                      <Skeleton className="h-8 w-24" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  ))
                : widget.content}
            </article>
          );
        })}
      </div>
      {state === 'loading' && (
        <p role="status" className="text-xs text-fg-muted">
          Загружаем значения виджетов…
        </p>
      )}
      {canEdit && (
        <p className="text-xs text-fg-muted">
          Меняй порядок кнопками «выше» и «ниже», в том числе с клавиатуры. На узком экране виджеты стоят в
          одну колонку.
        </p>
      )}
      {readOnly && editing && <p className="text-xs text-fg-muted">Настройка недоступна: только просмотр.</p>}
    </section>
  );
}
